import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { DistributorID } from 'fym-shared/src/smartlinks/catalog';
import {
  StreamingOverviewResponse,
  StreamingSectionKey,
  StreamingSource,
  StreamingTotalKey,
} from 'fym-shared/src/music24/streaming';
import type { ChartTokens } from '../theme/useChartTokens';

/**
 * The names and marks of the Streaming sources (design your-intelligence §11: source mark).
 * Brand names, so they are not translated.
 */
export interface StreamingSourceLook {
  /** The two-letter mark. */
  code: string;
  /** The long name: "Spotify for Artists". */
  name: string;
  /** The short name for the "from" line of a tile: "Spotify". */
  shortName: string;
  /** The chart ink of its line (`useChartTokens`). */
  chartKey: keyof ChartTokens;
}

const LOOKS: Record<string, StreamingSourceLook> = {
  [MusicAPIIntegrationID.Spotify]: {
    code: 'SP',
    name: 'Spotify for Artists',
    shortName: 'Spotify',
    chartKey: 'pos',
  },
  [MusicAPIIntegrationID.Deezer]: {
    code: 'DZ',
    name: 'Deezer for Creators',
    shortName: 'Deezer',
    chartKey: 'brandLift',
  },
  [MusicAPIIntegrationID.YoutubeMusic]: {
    code: 'YT',
    name: 'YouTube',
    shortName: 'YouTube',
    chartKey: 'neg',
  },
  [MusicAPIIntegrationID.SoundCloud]: {
    code: 'SC',
    name: 'SoundCloud',
    shortName: 'SoundCloud',
    chartKey: 'flat',
  },
  // The connect waits for the web flow (spec 2026-10-16 Audiomack, section 18); the numbers can show.
  [MusicAPIIntegrationID.Audiomack]: {
    code: 'AM',
    name: 'Audiomack',
    shortName: 'Audiomack',
    chartKey: 'line',
  },
  [DistributorID.Ditto]: { code: 'DT', name: 'Ditto', shortName: 'Ditto', chartKey: 'brand' },
  [DistributorID.TuneCore]: { code: 'TC', name: 'TuneCore', shortName: 'TuneCore', chartKey: 'brandLift' },
  // CD Baby Trending (2026-10-18): Spotify and Apple Music streams, iTunes downloads. Every data ink
  // is taken by a source that can be connected with it, so it draws in the neutral text ink.
  [DistributorID.CDBaby]: { code: 'CB', name: 'CD Baby', shortName: 'CD Baby', chartKey: 'tx1' },
  // UnitedMasters Insights (2026-10-23): the daily streams of each store and each track.
  [DistributorID.UnitedMasters]: { code: 'UM', name: 'UnitedMasters', shortName: 'UnitedMasters', chartKey: 'tx2' },
};

/** The look of a source. An unknown source gets its id as the name. */
export function sourceLook(integrationType: string): StreamingSourceLook {
  return (
    LOOKS[integrationType] ?? {
      code: integrationType.slice(0, 2).toUpperCase(),
      name: integrationType,
      shortName: integrationType,
      chartKey: 'axis',
    }
  );
}

/** The sources of an artist portal that give listeners, saves, playlist adds and followers. */
const LISTENER_PORTALS: readonly string[] = [MusicAPIIntegrationID.Spotify, MusicAPIIntegrationID.Deezer];

/** SoundCloud gives followers too (lifetime totals only). */
const FOLLOWER_SOURCES: readonly string[] = [...LISTENER_PORTALS, MusicAPIIntegrationID.SoundCloud];

/**
 * The sources that can feed a tile. A tile is drawn when one of them is connected (design §4.2:
 * "A total whose source is not connected is not drawn"). `null` means every source: the tile is
 * drawn as soon as any source is connected, with a dash when no connected source gives it.
 */
const TILE_SOURCES: Record<StreamingTotalKey, readonly string[] | null> = {
  streams: null,
  listeners: null,
  followers: FOLLOWER_SOURCES,
  subscribers: [MusicAPIIntegrationID.YoutubeMusic],
  saves: LISTENER_PORTALS,
  playlistAdds: LISTENER_PORTALS,
  monthlyListeners: LISTENER_PORTALS,
  downloads: [DistributorID.Ditto],
};

/** True when the tile of this total is drawn for these connected sources. */
export function isTileDrawn(key: StreamingTotalKey, sources: StreamingSource[]): boolean {
  if (sources.length === 0) {
    return false;
  }
  const feeds = TILE_SOURCES[key];
  return feeds === null || sources.some((source) => feeds.includes(source.integrationType));
}

/** True when an artist portal (not a distributor) is connected. The second row needs one. */
export function hasPortal(sources: StreamingSource[]): boolean {
  return sources.some((source) => source.kind === 'portal');
}

/** The sections of the split and the money (before 2026-10-16). The sources note is about these. */
const SPLIT_SECTION_KEYS: readonly StreamingSectionKey[] = ['stores', 'countries', 'earnings'];

/**
 * The connected sources that give a section (Stores, Countries, Earnings). Every connected source is
 * a source of the same kinds of data (owner decision 2026-10-06): Spotify for Artists alone gives a
 * Stores row. An old server has no `sections`: then only a distributor gives the three sections.
 */
export function sectionSources(
  overview: Pick<StreamingOverviewResponse, 'sections' | 'sources' | 'hasDistributor'>,
  section: StreamingSectionKey
): string[] {
  if (overview.sections !== undefined) {
    // A server from before 2026-10-16 has no key for the new sections: no source gives them.
    return overview.sections[section]?.sources ?? [];
  }
  if (!SPLIT_SECTION_KEYS.includes(section)) {
    return [];
  }
  return overview.hasDistributor
    ? overview.sources.filter((source) => source.kind === 'distributor').map((source) => source.integrationType)
    : [];
}

/** True when at least one connected source gives the data of the section. */
export function isSectionAvailable(
  overview: Pick<StreamingOverviewResponse, 'sections' | 'sources' | 'hasDistributor'>,
  section: StreamingSectionKey
): boolean {
  return sectionSources(overview, section).length > 0;
}

/**
 * True when the line "Each section shows what your connected sources report" shows: a source is
 * connected and at least one section has no source.
 */
export function showsSourcesNote(
  overview: Pick<StreamingOverviewResponse, 'sections' | 'sources' | 'hasDistributor'>
): boolean {
  return overview.sources.length > 0 && SPLIT_SECTION_KEYS.some((key) => !isSectionAvailable(overview, key));
}

/** The kinds of source that can give a section, for the empty line. An old server: the distributor. */
export function sectionGivenBy(
  overview: Pick<StreamingOverviewResponse, 'sections'>,
  section: StreamingSectionKey
): string[] {
  const sources = overview.sections?.[section];
  if (sources !== undefined) {
    return sources.givenBy;
  }
  return SPLIT_SECTION_KEYS.includes(section) ? [DistributorID.Ditto] : [];
}

/** The sources of the amounts of the earnings. An old server names none: then Ditto. */
export function earningsSources(earnings: { sources?: string[] }): string[] {
  return earnings.sources ?? [DistributorID.Ditto];
}

/** The names of some sources, joined: "Ditto", "Spotify for Artists, Ditto". Brand names. */
export function sourceNames(integrationTypes: readonly string[], length: 'name' | 'shortName' = 'name'): string {
  return integrationTypes.map((type) => sourceLook(type)[length]).join(', ');
}
